import React from 'react';
import { Link } from 'react-router-dom';
import { Wordmark } from '../components';

const LegalPage = ({ title, children }) => (
  <div style={{ minHeight: '100vh', background: 'var(--bg)', color: 'var(--ink)' }}>
    <header style={{
      position: 'sticky', top: 0, zIndex: 100,
      borderBottom: '1px solid var(--line)',
      background: 'oklch(1 0 0 / 0.85)',
      backdropFilter: 'blur(12px)',
      padding: '0 24px',
    }}>
      <div style={{ maxWidth: 800, margin: '0 auto', height: 60, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Link to="/" style={{ textDecoration: 'none' }}>
          <Wordmark size={22} />
        </Link>
        <Link to="/"
          style={{ fontSize: 13, color: 'var(--ink-2)', textDecoration: 'none', fontFamily: 'var(--font-mono)', letterSpacing: '.06em' }}>
          ← Back to home
        </Link>
      </div>
    </header>

    <div style={{ maxWidth: 800, margin: '0 auto', padding: '60px 24px 100px' }}>
      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '.14em', textTransform: 'uppercase', color: 'var(--ink-3)', marginBottom: 12 }}>Legal</div>
      <h1 style={{ fontSize: 'clamp(28px, 4vw, 48px)', marginBottom: 12, letterSpacing: '-0.02em' }}>{title}</h1>
      <p style={{ fontSize: 13, color: 'var(--ink-3)', fontFamily: 'var(--font-mono)', marginBottom: 48 }}>Last updated: June 2026</p>
      <div style={{ fontSize: 15, color: 'var(--ink-2)', lineHeight: 1.8 }}>
        {children}
      </div>
    </div>

    <footer style={{ borderTop: '1px solid var(--line)', padding: '24px', textAlign: 'center', fontSize: 12, color: 'var(--ink-4)', fontFamily: 'var(--font-mono)' }}>
      © 2026 VentyVIP
    </footer>
  </div>
);

const Section = ({ title, children }) => (
  <div style={{ marginBottom: 40 }}>
    <h2 style={{ fontSize: 18, fontWeight: 600, color: 'var(--ink)', marginBottom: 12, marginTop: 0 }}>{title}</h2>
    {children}
  </div>
);

const CookieTable = ({ cookies }) => (
  <div style={{ overflowX: 'auto', marginTop: 16, marginBottom: 24 }}>
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
      <thead>
        <tr style={{ borderBottom: '2px solid var(--line)' }}>
          <th style={{ textAlign: 'left', padding: '12px 8px', fontWeight: 600 }}>Cookie</th>
          <th style={{ textAlign: 'left', padding: '12px 8px', fontWeight: 600 }}>Purpose</th>
          <th style={{ textAlign: 'left', padding: '12px 8px', fontWeight: 600 }}>Duration</th>
        </tr>
      </thead>
      <tbody>
        {cookies.map((c, i) => (
          <tr key={i} style={{ borderBottom: '1px solid var(--line)' }}>
            <td style={{ padding: '12px 8px', fontFamily: 'var(--font-mono)', fontSize: 12 }}>{c.name}</td>
            <td style={{ padding: '12px 8px' }}>{c.purpose}</td>
            <td style={{ padding: '12px 8px' }}>{c.duration}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const Cookies = () => (
  <LegalPage title="Cookie Policy">
    <Section title="What Are Cookies">
      <p>Cookies are small text files stored on your device when you visit websites. They help websites function properly, improve user experience, and provide information to site owners. Similar technologies include web beacons, pixels, and local storage.</p>
    </Section>

    <Section title="How We Use Cookies">
      <p>VentyVIP uses cookies and similar technologies for the following purposes:</p>
      <ul style={{ paddingLeft: 20, marginTop: 12, display: 'grid', gap: 8 }}>
        <li><strong>Essential cookies</strong> — Required for the website to function (e.g., cookie consent preferences)</li>
        <li><strong>Functional cookies</strong> — Remember your preferences and settings</li>
        <li><strong>Analytics cookies</strong> — Help us understand how visitors interact with our site</li>
        <li><strong>Advertising cookies</strong> — Used by our advertising partners (Google AdSense, Google Ads) to deliver relevant ads and measure ad performance</li>
        <li><strong>Tracking pixels</strong> — Used by Meta (Facebook/Instagram) Pixel to measure ad effectiveness and enable ad targeting on Meta platforms</li>
      </ul>
    </Section>

    <Section title="Cookies We Use">
      <h3 style={{ fontSize: 16, marginTop: 24, marginBottom: 12 }}>Essential Cookies</h3>
      <CookieTable cookies={[
        { name: 'ventyvip_cookies', purpose: 'Stores your cookie consent preference', duration: '1 year' },
        { name: 'ventyvip_prefs', purpose: 'Stores your UI preferences', duration: '1 year' },
      ]} />

      <h3 style={{ fontSize: 16, marginTop: 24, marginBottom: 12 }}>Google Analytics</h3>
      <CookieTable cookies={[
        { name: '_ga', purpose: 'Distinguishes unique users by assigning a randomly generated number', duration: '2 years' },
        { name: '_ga_*', purpose: 'Used by Google Analytics to persist session state', duration: '2 years' },
        { name: '_gid', purpose: 'Distinguishes users for analytics purposes', duration: '24 hours' },
        { name: '_gat', purpose: 'Throttles request rate to Google Analytics', duration: '1 minute' },
      ]} />

      <h3 style={{ fontSize: 16, marginTop: 24, marginBottom: 12 }}>Google AdSense / Google Ads</h3>
      <CookieTable cookies={[
        { name: '__gads', purpose: 'Measures ad interactions and prevents showing the same ad repeatedly', duration: '13 months' },
        { name: '__gpi', purpose: 'Used for ad personalization across Google services', duration: '13 months' },
        { name: 'IDE', purpose: 'Used by Google DoubleClick for ad targeting and conversion tracking', duration: '13 months' },
        { name: 'DSID', purpose: 'Used to identify a signed-in user on non-Google sites for ad personalization', duration: '2 weeks' },
        { name: 'NID', purpose: 'Stores preferences and other information, such as preferred language and ad personalization', duration: '6 months' },
        { name: '_gcl_au', purpose: 'Google Ads conversion linker — stores conversion data', duration: '3 months' },
        { name: '_gcl_aw', purpose: 'Google Ads click identifier — tracks ad clicks for conversion measurement', duration: '3 months' },
      ]} />

      <h3 style={{ fontSize: 16, marginTop: 24, marginBottom: 12 }}>Meta (Facebook/Instagram) Pixel</h3>
      <CookieTable cookies={[
        { name: '_fbp', purpose: 'Used by Meta to deliver advertisements and measure their effectiveness', duration: '3 months' },
        { name: '_fbc', purpose: 'Stores Facebook click identifier for conversion tracking', duration: '3 months' },
        { name: 'fr', purpose: 'Used by Facebook for ad targeting and measurement', duration: '3 months' },
      ]} />
      <p style={{ marginTop: 8, fontSize: 13, color: 'var(--ink-3)' }}>Meta Pixel also uses web beacons (invisible tracking pixels) embedded in pages to track page views, button clicks, and other conversion events for ad measurement purposes.</p>

      <h3 style={{ fontSize: 16, marginTop: 24, marginBottom: 12 }}>Other Third-Party Ad Networks</h3>
      <p>VentyVIP may display ads from additional advertising networks. These networks may set their own cookies and tracking technologies. Each network operates under its own privacy policy.</p>
    </Section>

    <Section title="Your Cookie Choices">
      <p>You have the right to accept or reject non-essential cookies. When you first visit VentyVIP, you'll see a cookie banner where you can:</p>
      <ul style={{ paddingLeft: 20, marginTop: 12, display: 'grid', gap: 8 }}>
        <li><strong>Accept all cookies</strong> — Allows all cookie types including advertising and analytics</li>
        <li><strong>Essential Only</strong> — Only essential cookies will be used; advertising and analytics cookies will be blocked</li>
      </ul>
      <p style={{ marginTop: 16 }}>You can change your preference at any time by clearing your browser cookies and revisiting our site.</p>

      <p style={{ marginTop: 16 }}><strong>Opt out of personalized advertising:</strong></p>
      <ul style={{ paddingLeft: 20, marginTop: 8, display: 'grid', gap: 6 }}>
        <li><a href="https://adssettings.google.com" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary)' }}>Google Ads Settings</a> — control Google ad personalization</li>
        <li><a href="https://www.facebook.com/adpreferences" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary)' }}>Meta Ad Preferences</a> — control Facebook/Instagram ad personalization</li>
        <li><a href="https://optout.aboutads.info" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary)' }}>DAA Opt-Out</a> — opt out of interest-based advertising (US)</li>
        <li><a href="https://www.youronlinechoices.eu" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary)' }}>Your Online Choices</a> — manage online ad preferences (EU)</li>
        <li><a href="https://optout.networkadvertising.org" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary)' }}>NAI Opt-Out</a> — Network Advertising Initiative opt-out tool</li>
      </ul>
    </Section>

    <Section title="Managing Cookies in Your Browser">
      <p>Most web browsers allow you to control cookies through their settings. You can typically:</p>
      <ul style={{ paddingLeft: 20, marginTop: 12, display: 'grid', gap: 6 }}>
        <li>View cookies stored on your device</li>
        <li>Delete specific cookies or all cookies</li>
        <li>Block cookies from specific websites</li>
        <li>Block all cookies (note: this may affect site functionality)</li>
      </ul>
      <p style={{ marginTop: 16 }}>Visit your browser's help section to learn how to manage cookie settings:</p>
      <ul style={{ paddingLeft: 20, marginTop: 8, display: 'grid', gap: 4 }}>
        <li><a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary)' }}>Google Chrome</a></li>
        <li><a href="https://support.mozilla.org/en-US/kb/cookies-information-websites-store-on-your-computer" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary)' }}>Mozilla Firefox</a></li>
        <li><a href="https://support.apple.com/guide/safari/manage-cookies-sfri11471/mac" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary)' }}>Safari (Mac)</a></li>
        <li><a href="https://support.microsoft.com/en-us/microsoft-edge/delete-cookies-in-microsoft-edge-63947406-40ac-c3b8-8b2e-9b50b1a1eafb" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary)' }}>Microsoft Edge</a></li>
      </ul>
    </Section>

    <Section title="More Information">
      <p>To learn more about cookies and online tracking:</p>
      <ul style={{ paddingLeft: 20, marginTop: 8, display: 'grid', gap: 4 }}>
        <li><a href="https://www.allaboutcookies.org" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary)' }}>allaboutcookies.org</a> — general information about cookies</li>
        <li><a href="https://www.youronlinechoices.eu" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary)' }}>youronlinechoices.eu</a> — EU cookie and ad choices</li>
        <li><a href="https://policies.google.com/technologies/cookies" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary)' }}>Google's Cookie Policy</a></li>
        <li><a href="https://www.facebook.com/policies/cookies/" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary)' }}>Meta's Cookie Policy</a></li>
      </ul>
    </Section>

    <Section title="Contact Us">
      <p>If you have questions about our cookie policy or how we use cookies, please contact us:</p>
      <p style={{ marginTop: 8 }}><strong>Email:</strong> <a href="mailto:info@VentyVIP.com" style={{ color: 'var(--primary)' }}>info@VentyVIP.com</a></p>
      <p style={{ marginTop: 4 }}><strong>Website:</strong> <a href="https://ventyvip.com" style={{ color: 'var(--primary)' }}>ventyvip.com</a></p>
    </Section>
  </LegalPage>
);
